import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import CommunityDetail from './CommunityDetail';
import MyRaces from './MyRaces';
import MyProfile from './MyProfile';
import Settings from './Settings';
import Communities from './Communities';
import PublicProfile from './PublicProfile';
import ActivityFeed from './ActivityFeed';
import PrivacyPolicy from './PrivacyPolicy';
import Support from './Support';
import ReferralDashboard from './ReferralDashboard';
import PioneerLaunchPage from './PioneerLaunchPage';
import RaceGuide from './RaceGuide';
import NotificationDropdown from './NotificationDropdown';
import MyPosts from './MyPosts';
import Blog from './Blog';
import BlogPost from './BlogPost';
import { API_URL } from './config';
import { formatDate } from './utils';
import { useAuth } from './AuthContext';
import api from './api';
import fallbackRaces from './data/races.json';
import useIsMobile from './hooks/useIsMobile';

const CommunityBrowser = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, register, updateProfile } = useAuth();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(() => {
    const raceType = localStorage.getItem('runrora_race_type');
    const distance = localStorage.getItem('runrora_preferred_distance');
    if (raceType === 'triathlons') return 'ironman';
    if (raceType === 'runs' && distance && distance !== 'all') return distance;
    return 'all';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [registeredRaceIds, setRegisteredRaceIds] = useState(new Set());
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [showMyRaces, setShowMyRaces] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showCommunities, setShowCommunities] = useState(false);
  const [showFeed, setShowFeed] = useState(false);
  const [showAthletes, setShowAthletes] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState('');
  const [athleteResults, setAthleteResults] = useState([]);
  const [athleteSearching, setAthleteSearching] = useState(false);
  const [followingIds, setFollowingIds] = useState(new Set());
  const [showProfile, setShowProfile] = useState(false);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showReferralDashboard, setShowReferralDashboard] = useState(false);
  const [showPioneerPage, setShowPioneerPage] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [viewingUserId, setViewingUserId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterConfirm, setShowRegisterConfirm] = useState(null);
  const [isSignUp, setIsSignUp] = useState(true);
  const [loginName, setLoginName] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginCity, setLoginCity] = useState('');
  const [authError, setAuthError] = useState('');
  const [reviewSummaries, setReviewSummaries] = useState({});
  const [racePreviews, setRacePreviews] = useState({});
  const [charityName, setCharityName] = useState('');
  const [fundraisingUrl, setFundraisingUrl] = useState('');
  const [showCharityFields, setShowCharityFields] = useState(false);
  const [raceReadyMode, setRaceReadyMode] = useState(() => {
    return localStorage.getItem('runrora_race_ready') === 'true';
  });
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [guideRaceId, setGuideRaceId] = useState(null);
  const [showBlog, setShowBlog] = useState(false);
  const [blogSlug, setBlogSlug] = useState(null);

  const isMobile = useIsMobile();
  const isLoggedIn = !!user;

  // --- URL-based navigation ---
  const communitiesRef = useRef([]);
  const isLoggedInRef = useRef(false);
  const [routeInitialized, setRouteInitialized] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  communitiesRef.current = communities;
  isLoggedInRef.current = isLoggedIn;

  // Parse path and apply navigation state
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyPath = useCallback((path) => {
    setShowProfile(false);
    setShowSettings(false);
    setShowPrivacy(false);
    setShowSupport(false);
    setShowReferralDashboard(false);
    setShowPioneerPage(false);
    setGuideRaceId(null);
    setViewingUserId(null);
    setSelectedCommunity(null);
    setShowMyRaces(false);
    setShowCommunities(false);
    setShowFeed(false);
    setShowAthletes(false);
    setShowBlog(false);
    setBlogSlug(null);
    if (path === '/' || path === '') return;
    if (path === '/privacy') { setShowPrivacy(true); return; }
    if (path === '/support') { setShowSupport(true); return; }
    if (path === '/pioneers') { setShowPioneerPage(true); return; }
    if (path === '/blog') { setShowBlog(true); return; }
    if (path.startsWith('/blog/')) { const s = path.slice(6); if (s) { setShowBlog(true); setBlogSlug(s); } return; }
    const loggedIn = isLoggedInRef.current;
    const protectedPaths = ['/profile', '/settings', '/my-races', '/communities', '/feed', '/athletes', '/referrals'];
    if (protectedPaths.includes(path) && !loggedIn) {
      setPendingRedirect(path);
      setShowLoginModal(true);
      return;
    }
    if (path === '/profile') setShowProfile(true);
    else if (path === '/settings') setShowSettings(true);
    else if (path.startsWith('/athlete/')) {
      const id = parseInt(path.split('/')[2], 10);
      if (id) setViewingUserId(id);
    } else if (path.startsWith('/city/')) {
      const cityName = decodeURIComponent(path.split('/').slice(2).join('/'));
      if (cityName) {
        setSelectedCommunity({
          communityType: 'city',
          cityName,
          name: `${cityName} Running`,
        });
      }
    } else if (path.match(/^\/race\/\d+\/guide$/)) {
      const id = parseInt(path.split('/')[2], 10);
      if (id) setGuideRaceId(id);
    } else if (path.startsWith('/race/')) {
      const id = parseInt(path.split('/')[2], 10);
      if (id) {
        const found = communitiesRef.current.find(c => c.id === id);
        if (found) setSelectedCommunity(found);
      }
    } else if (path === '/my-races') setShowMyRaces(true);
    else if (path === '/communities') setShowCommunities(true);
    else if (path === '/feed') setShowFeed(true);
    else if (path === '/athletes') setShowAthletes(true);
    else if (path === '/referrals') setShowReferralDashboard(true);
    else if (path.startsWith('/join')) {
      // Parse referral code from /join?ref=PIONEER-xxx
      const params = new URLSearchParams(path.split('?')[1] || '');
      const ref = params.get('ref');
      if (ref) setReferralCode(ref);
      setShowLoginModal(true);
      setIsSignUp(true);
    }
  }, []);

  // State → URL sync
  useEffect(() => {
    if (!routeInitialized) return;
    let nextPath = '/';
    if (showPrivacy) nextPath = '/privacy';
    else if (showSupport) nextPath = '/support';
    else if (showPioneerPage) nextPath = '/pioneers';
    else if (showReferralDashboard) nextPath = '/referrals';
    else if (showProfile) nextPath = '/profile';
    else if (showSettings) nextPath = '/settings';
    else if (guideRaceId) nextPath = `/race/${guideRaceId}/guide`;
    else if (viewingUserId) nextPath = `/athlete/${viewingUserId}`;
    else if (selectedCommunity && selectedCommunity.communityType === 'city') nextPath = `/city/${encodeURIComponent(selectedCommunity.cityName)}`;
    else if (selectedCommunity) nextPath = `/race/${selectedCommunity.id}`;
    else if (showMyRaces) nextPath = '/my-races';
    else if (showCommunities) nextPath = '/communities';
    else if (showFeed) nextPath = '/feed';
    else if (showAthletes) nextPath = '/athletes';
    else if (showBlog && blogSlug) nextPath = `/blog/${blogSlug}`;
    else if (showBlog) nextPath = '/blog';
    if (window.location.pathname !== nextPath) {
      navigate(nextPath);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeInitialized, showPrivacy, showSupport, showPioneerPage, showReferralDashboard, showProfile, showSettings, guideRaceId, viewingUserId, selectedCommunity, showMyRaces, showCommunities, showFeed, showAthletes, showBlog, blogSlug]);

  // React Router handles back/forward — sync state from location changes
  useEffect(() => {
    if (routeInitialized) {
      applyPath(location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Initialize from URL path on first load
  useEffect(() => {
    if (routeInitialized) return;
    const path = location.pathname;
    if (path.startsWith('/race/') && !path.startsWith('/race-') && communities.length === 0) return;
    if (path && path !== '/') {
      applyPath(path);
    } else if (user) {
      // Logged-in users default to feed on home page
      setShowFeed(true);
    }
    // Also check for referral code in search params
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
      setShowLoginModal(true);
      setIsSignUp(true);
    }
    setRouteInitialized(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communities, routeInitialized]);

  // Navigate to pending redirect after login
  useEffect(() => {
    if (isLoggedIn && pendingRedirect) {
      applyPath(pendingRedirect);
      setPendingRedirect(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, pendingRedirect]);

  // Toggle Race-Ready Mode
  const toggleRaceReadyMode = () => {
    const newValue = !raceReadyMode;
    setRaceReadyMode(newValue);
    localStorage.setItem('runrora_race_ready', newValue.toString());

    if (newValue) {
      setShowMyRaces(true);
      setShowProfileDropdown(false);
    }
  };

  // Auto-redirect to My Races if Race-Ready mode is on
  useEffect(() => {
    if (isLoggedIn && raceReadyMode && !showMyRaces) {
      // Don't override explicit URL navigation
      if (location.pathname === '/') {
        setShowMyRaces(true);
      }
    }
  }, [isLoggedIn, raceReadyMode, showMyRaces, location.pathname]);

  const filters = [
    { key: 'all', label: 'All Races' },
    { key: 'marathon', label: 'Marathon' },
    { key: 'half', label: 'Half Marathon' },
    { key: 'ultra', label: 'Ultramarathon' },
    { key: 'ironman', label: 'Ironman' },
    { key: '10k', label: '10k' },
    { key: '5k', label: '5k' }
  ];

  useEffect(() => {
    fetchCommunities();
    fetchReviewSummaries();
    if (isLoggedIn) {
      fetchRegistrations();
      api.get('/registrations/previews').then(res => setRacePreviews(res.data)).catch(() => {});
    }

    // Prompt signup after 5 seconds if not logged in
    if (!isLoggedIn) {
      const timer = setTimeout(() => {
        if (!user) {
          setShowLoginModal(true);
        }
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, user]);

  const fetchCommunities = async () => {
    try {
      const response = await axios.get(`${API_URL}/races`);

      const communitiesData = response.data.map(race => ({
        id: race.id,
        name: race.name,
        location: race.location,
        end_date: race.date,
        image_url: race.image,
        type: race.type,
        description: race.description,
        source: race.source || null,
        verified: race.verified || false,
        external_url: race.external_url || null,
        is_sub_half: race.is_sub_half || false,
        city_name: race.city_name || null
      }));

      communitiesData.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
      setCommunities(communitiesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching communities, using local data:', error);
      const communitiesData = fallbackRaces.map(race => ({
        id: race.id,
        name: race.name,
        location: race.location,
        end_date: race.date,
        image_url: race.image,
        type: race.type,
        description: race.description,
        source: race.source || null,
        verified: race.verified || false,
        external_url: race.external_url || null,
        is_sub_half: false,
        city_name: null
      }));

      communitiesData.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
      setCommunities(communitiesData);
      setLoading(false);
    }
  };

  const fetchRegistrations = async () => {
    try {
      const res = await api.get('/registrations');
      const ids = new Set(res.data.map(r => r.raceId));
      setRegisteredRaceIds(ids);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const fetchReviewSummaries = async () => {
    try {
      const res = await axios.get(`${API_URL}/reviews/summaries`);
      setReviewSummaries(res.data);
    } catch (error) {
      // Reviews may not be available yet
    }
  };

  const handleRegister = async (community) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    const isRegistered = registeredRaceIds.has(community.id);

    if (isRegistered) {
      try {
        await api.delete(`/registrations/${community.id}`);
        setRegisteredRaceIds(prev => {
          const next = new Set(prev);
          next.delete(community.id);
          return next;
        });
      } catch (error) {
        console.error('Error unregistering:', error);
      }
    } else {
      if (community.external_url) {
        window.open(community.external_url, '_blank');
      }
      setShowRegisterConfirm(community);
    }
  };

  const handleConfirmRegistration = async (confirmed) => {
    if (confirmed && showRegisterConfirm) {
      try {
        const payload = { raceId: showRegisterConfirm.id };
        if (charityName.trim()) payload.charity_name = charityName.trim();
        if (fundraisingUrl.trim()) payload.fundraising_url = fundraisingUrl.trim();
        await api.post('/registrations', payload);
        setRegisteredRaceIds(prev => new Set(prev).add(showRegisterConfirm.id));
      } catch (error) {
        console.error('Error registering:', error);
      }
    }
    setShowRegisterConfirm(null);
    setCharityName('');
    setFundraisingUrl('');
    setShowCharityFields(false);
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');

    try {
      if (isSignUp) {
        if (!loginName.trim() || !loginEmail.trim() || !loginPassword) return;
        await register(loginName.trim(), loginEmail.trim(), loginPassword, loginCity, referralCode);
      } else {
        if (!loginEmail.trim() || !loginPassword) return;
        await login(loginEmail.trim(), loginPassword);
      }
      setShowLoginModal(false);
      setLoginName('');
      setLoginEmail('');
      setLoginPassword('');
      setLoginCity('');
      setAuthError('');
      setReferralCode(null);
      fetchRegistrations();
      // Navigate to feed after login
      setShowFeed(true);
      setShowMyRaces(false);
      setShowCommunities(false);
      setShowAthletes(false);
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong';
      setAuthError(msg);
    }
  };

  const handleProtectedAction = (action) => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    action();
  };

  // Athlete search with debounce
  useEffect(() => {
    if (!athleteSearch.trim() || athleteSearch.trim().length < 2) {
      setAthleteResults([]);
      return;
    }
    setAthleteSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/auth/users/search?q=${encodeURIComponent(athleteSearch.trim())}`);
        setAthleteResults(res.data);
      } catch (err) {
        console.error('Athlete search error:', err);
      } finally {
        setAthleteSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteSearch]);

  // Fetch following list when athletes tab opens
  useEffect(() => {
    if (showAthletes && isLoggedIn) {
      api.get('/follows/following').then(res => {
        setFollowingIds(new Set(res.data.map(u => u.id)));
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAthletes, isLoggedIn]);

  const handleToggleFollow = async (userId) => {
    const isFollowing = followingIds.has(userId);
    try {
      if (isFollowing) {
        await api.delete(`/follows/${userId}`);
        setFollowingIds(prev => { const next = new Set(prev); next.delete(userId); return next; });
      } else {
        await api.post(`/follows/${userId}`);
        setFollowingIds(prev => new Set(prev).add(userId));
      }
    } catch (err) {
      console.error('Follow toggle error:', err);
    }
  };

  const filterCommunities = (community) => {
    if (!community || typeof community.name !== 'string' || community.name === '') {
      return false;
    }

    // Hide past races from Discover
    if (new Date(community.end_date) < new Date()) return false;

    const nameLower = community.name.toLowerCase();
    const locationLower = (community.location || '').toLowerCase();
    const searchLower = searchQuery.toLowerCase();

    if (searchQuery && !nameLower.includes(searchLower) && !locationLower.includes(searchLower)) {
      return false;
    }

    if (activeFilter === 'all') return true;
    if (activeFilter === 'marathon') return nameLower.includes('marathon') && !nameLower.includes('half') && !nameLower.includes('ultra');
    if (activeFilter === 'half') return nameLower.includes('half marathon');
    if (activeFilter === 'ultra') return nameLower.includes('ultra');
    if (activeFilter === 'ironman') return nameLower.includes('ironman') || nameLower.includes('triathlon');
    if (activeFilter === '10k') return nameLower.includes('10k');
    if (activeFilter === '5k') return nameLower.includes('5k');

    return true;
  };

  if (showPrivacy) {
    return <>
      <Helmet><title>Privacy Policy — RunRora</title><meta name="description" content="RunRora privacy policy. Learn how we handle your data." /></Helmet>
      <PrivacyPolicy onBack={() => setShowPrivacy(false)} />
    </>;
  }

  if (showSupport) {
    return <>
      <Helmet><title>Support — RunRora</title><meta name="description" content="Get help with RunRora. Contact our support team." /></Helmet>
      <Support onBack={() => setShowSupport(false)} />
    </>;
  }

  if (showBlog) {
    if (blogSlug) {
      return <BlogPost
        slug={blogSlug}
        onBack={() => { setShowBlog(false); setBlogSlug(null); }}
        onBlogHome={() => setBlogSlug(null)}
      />;
    }
    return <Blog
      onSelectPost={(slug) => setBlogSlug(slug)}
      onBack={() => setShowBlog(false)}
    />;
  }

  if (showPioneerPage) {
    return <>
      <Helmet>
        <title>Atlas Pioneer Elite — RunRora</title>
        <meta name="description" content="Join the first 100 founding members of RunRora. Earn your Atlas Pioneer Elite badge, unlock referral rewards, and help build the running community." />
        <meta property="og:title" content="Atlas Pioneer Elite — RunRora" />
        <meta property="og:description" content="Join the first 100 founding members of RunRora. Earn your Atlas Pioneer Elite badge and help build the running community." />
      </Helmet>
      <PioneerLaunchPage onBack={() => setShowPioneerPage(false)} onSignUp={() => { setShowPioneerPage(false); setShowLoginModal(true); setIsSignUp(true); }} />
    </>;
  }

  if (showReferralDashboard) {
    return <>
      <Helmet><title>Referral Dashboard — RunRora</title></Helmet>
      <ReferralDashboard onBack={() => setShowReferralDashboard(false)} user={user} />
    </>;
  }

  if (showMyPosts) {
    return <>
      <Helmet><title>My Posts — RunRora</title></Helmet>
      <MyPosts onBack={() => setShowMyPosts(false)} />
    </>;
  }

  if (showProfile) {
    return <>
      <Helmet><title>My Profile — RunRora</title></Helmet>
      <MyProfile user={user} onBack={() => setShowProfile(false)} onProfileUpdate={updateProfile} />
    </>;
  }

  if (showSettings) {
    return <>
    <Helmet><title>Settings — RunRora</title></Helmet>
    <Settings onBack={() => {
      // Re-apply filter from preferences when returning from Settings
      const raceType = localStorage.getItem('runrora_race_type');
      const distance = localStorage.getItem('runrora_preferred_distance');
      if (raceType === 'triathlons') {
        setActiveFilter('ironman');
      } else if (raceType === 'runs' && distance && distance !== 'all') {
        setActiveFilter(distance);
      } else {
        setActiveFilter('all');
      }
      setShowSettings(false);
    }} user={user} onProfileUpdate={updateProfile} />
    </>;
  }

  if (guideRaceId) {
    return <RaceGuide
      raceId={guideRaceId}
      onBack={() => setGuideRaceId(null)}
      onViewRace={(id) => {
        setGuideRaceId(null);
        const found = communitiesRef.current.find(c => c.id === id);
        if (found) setSelectedCommunity(found);
      }}
      onSignUp={!isLoggedIn ? () => { setIsSignUp(true); setShowLoginModal(true); } : null}
    />;
  }

  if (viewingUserId) {
    return <>
      <Helmet><title>Athlete Profile — RunRora</title><meta name="description" content="View this runner's profile on RunRora." /></Helmet>
      <PublicProfile userId={viewingUserId} onBack={() => setViewingUserId(null)} currentUserId={user?.id} />
    </>;
  }

  if (selectedCommunity) {
    const communityTitle = selectedCommunity.name || 'Community';
    const communityDesc = selectedCommunity.communityType === 'city'
      ? `Join the ${communityTitle} community on RunRora. Connect with local runners.`
      : `${communityTitle} on RunRora. See who's running, join the community, and connect with fellow runners.`;
    return <>
      <Helmet>
        <title>{communityTitle} — RunRora</title>
        <meta name="description" content={communityDesc} />
        <meta property="og:title" content={`${communityTitle} — RunRora`} />
        <meta property="og:description" content={communityDesc} />
      </Helmet>
      <CommunityDetail community={selectedCommunity} onBack={() => setSelectedCommunity(null)} user={user} onLoginRequired={() => setShowLoginModal(true)} onViewProfile={(id) => { setSelectedCommunity(null); setViewingUserId(id); }} />
    </>;
  }

  if (showMyRaces) {
    return <>
      <Helmet><title>My Races — RunRora</title><meta name="description" content="Your registered races on RunRora. Track your upcoming events." /></Helmet>
      <MyRaces
        onBack={() => setShowMyRaces(false)}
        raceReadyMode={raceReadyMode}
        onToggleRaceReady={toggleRaceReadyMode}
        user={user}
        onLogout={onLogout}
      />
    </>;
  }

  if (showCommunities) {
    return <>
      <Helmet><title>My Communities — RunRora</title><meta name="description" content="Your running communities on RunRora. Race groups and city running clubs." /></Helmet>
      <Communities onBack={() => setShowCommunities(false)} user={user} onLoginRequired={() => setShowLoginModal(true)} onViewProfile={(id) => { setShowCommunities(false); setViewingUserId(id); }} />
    </>;
  }

  if (showFeed) {
    return <>
      <Helmet><title>Activity Feed — RunRora</title><meta name="description" content="See what runners in your community are up to." /></Helmet>
      <ActivityFeed
        onBack={() => setShowFeed(false)}
        user={user}
        registeredRaceIds={registeredRaceIds}
        onRegister={handleRegister}
        onViewProfile={(id) => { setShowFeed(false); setViewingUserId(id); }}
      />
    </>;
  }

  if (showAthletes) {
    return (<>
      <Helmet><title>Find Athletes — RunRora</title><meta name="description" content="Search for runners on RunRora. Find training partners and connect with fellow athletes." /></Helmet>
      <div style={{ minHeight: '100vh', background: '#0B0B10', color: '#FFFFFF' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
          borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
          padding: '1rem 1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}>
          <button onClick={() => { setShowAthletes(false); setAthleteSearch(''); setAthleteResults([]); }} style={{ background: 'transparent', border: 'none', color: '#FF8A3D', fontSize: '1.5rem', cursor: 'pointer', padding: '0.25rem' }}>
            {'←'}
          </button>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Search Athletes</h1>
        </div>

        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '1.5rem' }}>
          {/* Search Input */}
          <div style={{ marginBottom: '1.5rem' }}>
            <input
              type="text"
              value={athleteSearch}
              onChange={(e) => setAthleteSearch(e.target.value)}
              placeholder="Search runners by name..."
              autoFocus
              style={{
                width: '100%',
                padding: '0.85rem 1.25rem',
                background: '#16161D',
                border: '1px solid rgba(255, 138, 61, 0.2)',
                borderRadius: '12px',
                color: '#FFFFFF',
                fontSize: '1rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Results */}
          {athleteSearching && (
            <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Searching...</p>
          )}

          {!athleteSearching && athleteSearch.trim().length >= 2 && athleteResults.length === 0 && (
            <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>No runners found</p>
          )}

          {!athleteSearching && athleteSearch.trim().length < 2 && athleteSearch.trim().length > 0 && (
            <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Type at least 2 characters to search</p>
          )}

          {!athleteSearching && athleteSearch.trim().length === 0 && (
            <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Search for runners by name to follow them and connect</p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {athleteResults.map(athlete => {
              const name = athlete.first_name
                ? `${athlete.first_name} ${athlete.last_name || ''}`.trim()
                : athlete.name || 'Runner';
              const initials = athlete.first_name
                ? (athlete.first_name[0] + (athlete.last_name?.[0] || '')).toUpperCase()
                : (athlete.name || 'R').substring(0, 2).toUpperCase();
              const isFollowing = followingIds.has(athlete.id);

              return (
                <div
                  key={athlete.id}
                  style={{
                    background: '#16161D',
                    border: '1px solid rgba(255, 138, 61, 0.1)',
                    borderRadius: '12px',
                    padding: '1rem 1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                  }}
                  onClick={() => { setShowAthletes(false); setViewingUserId(athlete.id); }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 138, 61, 0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 138, 61, 0.1)'; }}
                >
                  {/* Avatar */}
                  {athlete.profile_photo ? (
                    <img src={athlete.profile_photo} alt={`${athlete.first_name || athlete.name || 'Athlete'} avatar`} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '50%',
                      background: 'rgba(255, 138, 61, 0.2)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: '1rem', fontWeight: 700, color: '#FF8A3D', flexShrink: 0,
                    }}>
                      {initials}
                    </div>
                  )}

                  {/* Name */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '1rem' }}>{name}</span>
                      {athlete.verified_athlete && (
                        <span style={{ color: '#4ade80', fontSize: '0.75rem', fontWeight: 600, background: 'rgba(74, 222, 128, 0.15)', padding: '0.1rem 0.4rem', borderRadius: '8px' }}>
                          {'✓'} Verified
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Follow Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFollow(athlete.id);
                    }}
                    style={{
                      padding: '0.4rem 1rem',
                      background: isFollowing ? '#FF8A3D' : 'transparent',
                      border: isFollowing ? 'none' : '1px solid #FF8A3D',
                      color: isFollowing ? '#0B0B10' : '#FF8A3D',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {isFollowing ? 'Following' : 'Follow'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>);
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      color: '#FFFFFF'
    }}>
      {/* Header */}
      <nav aria-label="Main navigation" style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #16161D 100%)',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo */}
          <img
            src={`${process.env.PUBLIC_URL}/runrora-logo.png`}
            alt="Runrora"
            style={{
              height: '36px',
              width: 'auto',
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowMyRaces(false);
              setShowCommunities(false);
            }}
          />

          {/* Navigation */}
          <div style={{
            display: 'flex',
            gap: isMobile ? '0.5rem' : '2rem',
            alignItems: 'center'
          }}>
            {isMobile ? (
              <>
                {/* Hamburger menu button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
                  aria-expanded={showMobileMenu}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#FF8A3D',
                    cursor: 'pointer',
                    fontSize: '1.5rem',
                    padding: '0.4rem',
                  }}
                >
                  {showMobileMenu ? '✕' : '☰'}
                </button>

                {/* Notifications Bell (mobile) */}
                {isLoggedIn && (
                  <NotificationDropdown
                    onViewProfile={(userId) => {
                      setViewingUserId(userId);
                      setShowProfileDropdown(false);
                    }}
                    onViewThread={() => {}}
                  />
                )}

                {/* Settings Gear */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => {
                      if (!isLoggedIn) {
                        setShowLoginModal(true);
                      } else {
                        setShowProfileDropdown(!showProfileDropdown);
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      padding: '0.4rem',
                      color: showProfileDropdown ? '#FF8A3D' : '#8b9cb3',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    ⚙️
                  </button>

                  {showProfileDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '0.5rem',
                      background: '#16161D',
                      border: '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '12px',
                      padding: '0.5rem',
                      minWidth: '220px',
                      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                      zIndex: 100
                    }}>
                      {isLoggedIn ? (
                        <>
                          <button onClick={() => { setShowProfile(true); setShowProfileDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', color: '#FFFFFF', background: 'transparent', border: 'none', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', width: '100%', textAlign: 'left' }}><span>👤</span> My Profile</button>
                          <button onClick={() => { setShowMyPosts(true); setShowProfileDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', color: '#FFFFFF', background: 'transparent', border: 'none', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', width: '100%', textAlign: 'left' }}><span>📝</span> My Posts</button>
                          <button onClick={() => { setShowSettings(true); setShowProfileDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', color: '#FFFFFF', background: 'transparent', border: 'none', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', width: '100%', textAlign: 'left' }}><span>⚙️</span> Settings</button>
                          <div style={{ height: '1px', background: 'rgba(139, 156, 179, 0.2)', margin: '0.5rem 0' }} />
                          <button onClick={() => { onLogout(); setShowProfileDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', color: '#ff6b6b', background: 'transparent', border: 'none', borderRadius: '8px', fontSize: '0.9rem', cursor: 'pointer', width: '100%', textAlign: 'left' }}><span>🚪</span> Sign Out</button>
                        </>
                      ) : (
                        <button onClick={() => { setShowLoginModal(true); setShowProfileDropdown(false); }} style={{ width: '100%', padding: '0.75rem', background: '#FF8A3D', border: 'none', color: '#0B0B10', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>Get Started</button>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
            <button
              onClick={() => {
                setShowMyRaces(false);
                setShowCommunities(false);
                setShowAthletes(false);
                setShowFeed(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: (!showMyRaces && !showCommunities && !showAthletes && !showFeed && !showBlog) ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '0.5rem 0',
                borderBottom: (!showMyRaces && !showCommunities && !showAthletes && !showFeed && !showBlog) ? '2px solid #FF8A3D' : 'none'
              }}
            >
              Discover
            </button>

            <button
              onClick={() => handleProtectedAction(() => {
                setShowFeed(true);
                setShowMyRaces(false);
                setShowCommunities(false);
                setShowAthletes(false);
              })}
              style={{
                background: 'transparent',
                border: 'none',
                color: showFeed ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '0.5rem 0',
                borderBottom: showFeed ? '2px solid #FF8A3D' : 'none'
              }}
            >
              Feed
            </button>

            <button
              onClick={() => handleProtectedAction(() => {
                setShowMyRaces(true);
                setShowCommunities(false);
                setShowAthletes(false);
                setShowFeed(false);
              })}
              style={{
                background: 'transparent',
                border: 'none',
                color: showMyRaces ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '0.5rem 0',
                borderBottom: showMyRaces ? '2px solid #FF8A3D' : 'none'
              }}
            >
              My Races
            </button>

            <button
              onClick={() => handleProtectedAction(() => {
                setShowCommunities(true);
                setShowMyRaces(false);
                setShowAthletes(false);
                setShowFeed(false);
              })}
              style={{
                background: 'transparent',
                border: 'none',
                color: showCommunities ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '0.5rem 0',
                borderBottom: showCommunities ? '2px solid #FF8A3D' : 'none'
              }}
            >
              Communities
            </button>

            <button
              onClick={() => handleProtectedAction(() => {
                setShowAthletes(true);
                setShowMyRaces(false);
                setShowCommunities(false);
                setShowFeed(false);
              })}
              style={{
                background: 'transparent',
                border: 'none',
                color: showAthletes ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '0.5rem 0',
                borderBottom: showAthletes ? '2px solid #FF8A3D' : 'none'
              }}
            >
              Athletes
            </button>

            <button
              onClick={() => {
                setShowBlog(true);
                setShowMyRaces(false);
                setShowCommunities(false);
                setShowAthletes(false);
                setShowFeed(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: showBlog ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 600,
                padding: '0.5rem 0',
                borderBottom: showBlog ? '2px solid #FF8A3D' : 'none'
              }}
            >
              Blog
            </button>

            {/* Notifications Bell */}
            {isLoggedIn && (
              <NotificationDropdown
                onViewProfile={(userId) => {
                  setViewingUserId(userId);
                  setShowProfileDropdown(false);
                }}
                onViewThread={(threadId) => {
                  // Navigate to thread — handled by state
                }}
              />
            )}

            {/* Settings Gear / Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => {
                  if (!isLoggedIn) {
                    setShowLoginModal(true);
                  } else {
                    setShowProfileDropdown(!showProfileDropdown);
                  }
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  padding: '0.4rem',
                  color: showProfileDropdown ? '#FF8A3D' : '#8b9cb3',
                  transition: 'color 0.2s ease'
                }}
              >
                ⚙️
              </button>

              {showProfileDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '0.5rem',
                  background: '#16161D',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '12px',
                  padding: '0.5rem',
                  minWidth: '220px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                  zIndex: 100
                }}>
                  {isLoggedIn ? (
                    <>
                      {/* My Profile */}
                      <button
                        onClick={() => {
                          setShowProfile(true);
                          setShowProfileDropdown(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          color: '#FFFFFF',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>👤</span> My Profile
                      </button>

                      {/* My Posts */}
                      <button
                        onClick={() => {
                          setShowMyPosts(true);
                          setShowProfileDropdown(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          color: '#FFFFFF',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>📝</span> My Posts
                      </button>

                      {/* Race-Ready Mode Toggle */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRaceReadyMode();
                          setShowProfileDropdown(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem 1rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: raceReadyMode ? 'rgba(255, 138, 61, 0.15)' : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (!raceReadyMode) e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                        }}
                        onMouseLeave={(e) => {
                          if (!raceReadyMode) e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span>🏁</span>
                          <span style={{ color: raceReadyMode ? '#FF8A3D' : '#FFFFFF', fontSize: '0.9rem' }}>
                            Race-Ready Mode
                          </span>
                        </div>
                        <div style={{
                          width: '40px',
                          height: '22px',
                          borderRadius: '11px',
                          background: raceReadyMode ? '#FF8A3D' : 'rgba(139, 156, 179, 0.3)',
                          position: 'relative',
                          transition: 'background 0.2s ease'
                        }}>
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: '#fff',
                            position: 'absolute',
                            top: '2px',
                            left: raceReadyMode ? '20px' : '2px',
                            transition: 'left 0.2s ease',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }} />
                        </div>
                      </div>

                      {/* Groups */}
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          color: '#FFFFFF',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>👥</span> Groups
                      </button>

                      {/* Settings */}
                      <button
                        onClick={() => {
                          setShowSettings(true);
                          setShowProfileDropdown(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          color: '#FFFFFF',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>⚙️</span> Settings
                      </button>

                      {/* Divider */}
                      <div style={{
                        height: '1px',
                        background: 'rgba(139, 156, 179, 0.2)',
                        margin: '0.5rem 0'
                      }} />

                      {/* Sign Out */}
                      <button
                        onClick={() => {
                          onLogout();
                          setShowProfileDropdown(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          color: '#ff6b6b',
                          background: 'transparent',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          cursor: 'pointer',
                          width: '100%',
                          textAlign: 'left'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,107,107,0.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>🚪</span> Sign Out
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setShowLoginModal(true);
                        setShowProfileDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#FF8A3D',
                        border: 'none',
                        color: '#0B0B10',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 600
                      }}
                    >
                      Get Started
                    </button>
                  )}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      {isMobile && showMobileMenu && (
        <div style={{
          background: '#16161D',
          borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
          padding: '0.5rem 1rem',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: '60px',
          zIndex: 99,
        }}>
          {[
            { label: 'Discover', active: !showMyRaces && !showCommunities && !showAthletes && !showFeed && !showBlog, action: () => { setShowMyRaces(false); setShowCommunities(false); setShowAthletes(false); setShowFeed(false); setShowBlog(false); } },
            { label: 'Feed', active: showFeed, protected: true, action: () => { setShowFeed(true); setShowMyRaces(false); setShowCommunities(false); setShowAthletes(false); } },
            { label: 'My Races', active: showMyRaces, protected: true, action: () => { setShowMyRaces(true); setShowCommunities(false); setShowAthletes(false); setShowFeed(false); } },
            { label: 'Communities', active: showCommunities, protected: true, action: () => { setShowCommunities(true); setShowMyRaces(false); setShowAthletes(false); setShowFeed(false); } },
            { label: 'Athletes', active: showAthletes, protected: true, action: () => { setShowAthletes(true); setShowMyRaces(false); setShowCommunities(false); setShowFeed(false); } },
            { label: 'Blog', active: showBlog, action: () => { setShowBlog(true); setShowMyRaces(false); setShowCommunities(false); setShowAthletes(false); setShowFeed(false); } },
          ].map(item => (
            <button
              key={item.label}
              onClick={() => {
                if (item.protected) {
                  handleProtectedAction(item.action);
                } else {
                  item.action();
                }
                setShowMobileMenu(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: item.active ? '#FF8A3D' : '#8b9cb3',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                padding: '0.75rem 1rem',
                textAlign: 'left',
                borderLeft: item.active ? '3px solid #FF8A3D' : '3px solid transparent',
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem'
      }}>
        {/* Hero Section */}
        <div style={{
          textAlign: 'center',
          marginBottom: isMobile ? '1.5rem' : '3rem'
        }}>
          <h1 style={{
            fontSize: isMobile ? '1.75rem' : '2.5rem',
            fontWeight: 700,
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #FF8A3D 0%, #FFFFFF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {isLoggedIn ? 'Discover Your Next Race' : 'Race day starts here.'}
          </h1>
          <p style={{
            fontSize: isMobile ? '0.95rem' : '1.1rem',
            color: '#8b9cb3',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {isLoggedIn
              ? 'Connect with fellow runners, find travel companions, and join races around the world'
              : 'Build your race plan. Find your people. Show up ready.'}
          </p>
          {!isLoggedIn && (
            <button
              onClick={() => { setIsSignUp(true); setShowLoginModal(true); }}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 2rem',
                background: '#FF8A3D',
                color: '#0B0B10',
                border: 'none',
                borderRadius: '25px',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
            >
              Join free
            </button>
          )}
        </div>

        {/* Value Proposition — logged-out visitors only */}
        {!isLoggedIn && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '1.25rem',
            maxWidth: '900px',
            margin: `0 auto ${isMobile ? '2rem' : '3rem'}`,
          }}>
            <div style={{
              background: '#16161D',
              border: '1px solid rgba(255, 138, 61, 0.12)',
              borderRadius: '14px',
              padding: '1.5rem',
            }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#FFFFFF', margin: '0 0 0.6rem' }}>
                Build a real race plan
              </h3>
              <p style={{ fontSize: '0.88rem', color: '#8b9cb3', lineHeight: 1.6, margin: 0 }}>
                Pace splits. Nutrition timing. Travel logistics. A race-day timeline that tells you exactly where to be and when. Not a training log — a plan for the race itself.
              </p>
            </div>
            <div style={{
              background: '#16161D',
              border: '1px solid rgba(255, 138, 61, 0.12)',
              borderRadius: '14px',
              padding: '1.5rem',
            }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#FFFFFF', margin: '0 0 0.6rem' }}>
                Your race has a community
              </h3>
              <p style={{ fontSize: '0.88rem', color: '#8b9cb3', lineHeight: 1.6, margin: 0 }}>
                Every race on Runrora has its own board. Talk to people running the same race. Share your strategy. Find a travel buddy. Ask someone who ran it last year.
              </p>
            </div>
            <div style={{
              background: '#16161D',
              border: '1px solid rgba(255, 138, 61, 0.12)',
              borderRadius: '14px',
              padding: '1.5rem',
            }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#FFFFFF', margin: '0 0 0.6rem' }}>
                From signup to finish line
              </h3>
              <p style={{ fontSize: '0.88rem', color: '#8b9cb3', lineHeight: 1.6, margin: 0 }}>
                Checklist, travel planner, strategy notes, post-race reflection — everything in one place instead of scattered across notes apps and spreadsheets.
              </p>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div style={{
          marginBottom: '2rem',
          maxWidth: '600px',
          margin: '0 auto 2rem auto'
        }}>
          <input
            type="text"
            placeholder="Search races by name or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '1rem 1.5rem',
              background: '#16161D',
              border: '1px solid rgba(255, 138, 61, 0.2)',
              borderRadius: '12px',
              color: '#FFFFFF',
              fontSize: '1rem',
              outline: 'none',
              transition: 'all 0.3s ease'
            }}
          />
        </div>

        {/* Filters */}
        <div role="group" aria-label="Filter races by type" style={{
          display: 'flex',
          gap: '0.75rem',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          {filters.map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              aria-pressed={activeFilter === filter.key}
              style={{
                padding: '0.6rem 1.5rem',
                background: activeFilter === filter.key ? '#FF8A3D' : 'transparent',
                border: `1px solid ${activeFilter === filter.key ? '#FF8A3D' : 'rgba(255, 138, 61, 0.3)'}`,
                color: activeFilter === filter.key ? '#0B0B10' : '#8b9cb3',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 600,
                transition: 'all 0.2s ease'
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Race Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: isMobile ? '1rem' : '1.5rem',
          marginTop: '2rem'
        }}>
          {loading ? (
            <div role="status" aria-live="polite" style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '4rem' }}>
              <p style={{ color: '#8b9cb3' }}>Loading races...</p>
            </div>
          ) : (
            communities
              .filter(filterCommunities)
              .map(community => {
                const isRegistered = registeredRaceIds.has(community.id);

                return (
                  <article
                    key={community.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`${community.name}${community.location ? `, ${community.location}` : ''}${community.end_date ? `, ${formatDate(community.end_date)}` : ''}${isRegistered ? ' — Registered' : ''}`}
                    onClick={() => {
                      if (community.is_sub_half && community.city_name) {
                        setSelectedCommunity({
                          communityType: 'city',
                          cityName: community.city_name,
                          name: `${community.city_name} Running`,
                        });
                      } else {
                        setSelectedCommunity(community);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (community.is_sub_half && community.city_name) {
                          setSelectedCommunity({
                            communityType: 'city',
                            cityName: community.city_name,
                            name: `${community.city_name} Running`,
                          });
                        } else {
                          setSelectedCommunity(community);
                        }
                      }
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #16161D 0%, #1E1E28 100%)',
                      border: isRegistered ? '2px solid #FF8A3D' : '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative'
                    }}
                  >
                    {/* Race Image */}
                    <div aria-hidden="true" style={{
                      height: '180px',
                      backgroundImage: `url(${community.image_url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(to top, rgba(11, 11, 16, 0.95), transparent)',
                        padding: '1.5rem 1rem 0.75rem'
                      }}>
                        <h3 style={{
                          fontSize: '1.1rem',
                          fontWeight: 700,
                          color: '#FFFFFF',
                          marginBottom: '0.25rem'
                        }}>
                          {community.name}
                        </h3>
                      </div>
                    </div>

                    {/* Race Info */}
                    <div style={{ padding: '1rem' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                        color: '#8b9cb3',
                        fontSize: '0.85rem'
                      }}>
                        <span>📍</span>
                        <span>{community.location}</span>
                      </div>

                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.75rem',
                        color: '#8b9cb3',
                        fontSize: '0.85rem'
                      }}>
                        <span>📅</span>
                        <span>{formatDate(community.end_date)}</span>
                      </div>

                      {/* Review Rating */}
                      {reviewSummaries[community.id] && (
                        <div aria-label={`Rated ${reviewSummaries[community.id].avg_overall} out of 5 stars from ${reviewSummaries[community.id].review_count} reviews`} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          marginBottom: '0.75rem',
                          color: '#FF8A3D',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}>
                          <span aria-hidden="true">{reviewSummaries[community.id].avg_overall}</span>
                          <span aria-hidden="true">{'\u2605'}</span>
                          <span aria-hidden="true" style={{ color: '#8b9cb3', fontWeight: 400 }}>
                            ({reviewSummaries[community.id].review_count})
                          </span>
                        </div>
                      )}

                      {/* Source / Verified Badges */}
                      {(community.verified || (community.source && community.source !== 'manual')) && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.75rem',
                          flexWrap: 'wrap'
                        }}>
                          {community.verified && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              background: 'rgba(74, 222, 128, 0.15)',
                              color: '#4ade80',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              padding: '0.2rem 0.6rem',
                              borderRadius: '12px'
                            }}>
                              ✓ Verified
                            </span>
                          )}
                          {community.source && community.source !== 'manual' && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#8b9cb3',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              padding: '0.2rem 0.6rem',
                              borderRadius: '12px',
                              background: 'rgba(139, 156, 179, 0.1)'
                            }}>
                              {community.source}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Sub-half city badge */}
                      {community.is_sub_half && community.city_name && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          marginBottom: '0.75rem',
                          color: '#8b9cb3',
                          fontSize: '0.8rem',
                          fontStyle: 'italic',
                        }}>
                          Part of {community.city_name} Running
                        </div>
                      )}

                      {/* Registered User Avatars */}
                      {racePreviews[community.id] && racePreviews[community.id].registeredCount > 0 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '0.75rem'
                        }}>
                          {racePreviews[community.id].registeredUsersPreview.slice(0, 5).map((u, i) => (
                            <div key={u.id} style={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              border: '2px solid #0B0B10',
                              marginLeft: i > 0 ? '-8px' : 0,
                              zIndex: 5 - i,
                              position: 'relative',
                              overflow: 'hidden',
                              flexShrink: 0
                            }}>
                              {u.profilePhoto ? (
                                <img src={u.profilePhoto} alt={`${u.firstName || 'Runner'} avatar`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <div aria-label={`${u.firstName || 'Runner'} avatar`} style={{
                                  width: '100%',
                                  height: '100%',
                                  background: 'rgba(255, 138, 61, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.65rem',
                                  color: '#FF8A3D',
                                  fontWeight: 700
                                }}>
                                  {(u.firstName || 'R')[0].toUpperCase()}
                                </div>
                              )}
                            </div>
                          ))}
                          <span style={{
                            marginLeft: '0.5rem',
                            color: '#9ca3af',
                            fontSize: '0.8rem'
                          }}>
                            {racePreviews[community.id].registeredCount} registered
                          </span>
                        </div>
                      )}

                      {/* Save Button */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingTop: '0.75rem',
                        borderTop: '1px solid rgba(255, 138, 61, 0.1)'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRegister(community);
                          }}
                          aria-label={isRegistered ? `Registered for ${community.name}` : `Register for ${community.name}`}
                          style={{
                            padding: '0.5rem 1.25rem',
                            background: isRegistered ? '#FF8A3D' : 'transparent',
                            border: isRegistered ? 'none' : '1px solid #FF8A3D',
                            color: isRegistered ? '#0B0B10' : '#FF8A3D',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {isRegistered ? '✓ Registered' : (isLoggedIn ? 'Register' : 'Get started to register')}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })
          )}
        </div>

        {/* Bottom CTA — logged-out only */}
        {!isLoggedIn && (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem 1rem',
          }}>
            <span style={{ color: '#8b9cb3', fontSize: '1rem' }}>
              Stop winging race day.{' '}
            </span>
            <span
              onClick={() => { setIsSignUp(true); setShowLoginModal(true); }}
              style={{
                color: '#FF8A3D',
                fontWeight: 700,
                fontSize: '1rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              Sign up
            </span>
          </div>
        )}

        {/* Empty State */}
        {communities.filter(filterCommunities).length === 0 && !loading && (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#8b9cb3'
          }}>
            <h3 style={{ color: '#FFFFFF' }}>No races found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Registration Confirmation Modal */}
      {showRegisterConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowRegisterConfirm(null)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '20px',
              padding: '2.5rem',
              maxWidth: '420px',
              width: '90%',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              color: '#FFFFFF',
              marginBottom: '0.75rem',
              fontSize: '1.35rem'
            }}>
              Confirm Registration
            </h2>

            <p style={{
              color: '#8b9cb3',
              marginBottom: '0.5rem',
              lineHeight: 1.6,
              fontSize: '1rem'
            }}>
              {showRegisterConfirm.external_url
                ? `Did you complete registration for`
                : `Mark yourself as registered for`}
            </p>
            <p style={{
              color: '#FF8A3D',
              fontWeight: 600,
              fontSize: '1.1rem',
              marginBottom: '2rem'
            }}>
              {showRegisterConfirm.name}?
            </p>

            {/* Charity / Fundraising (#168) */}
            <div style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setShowCharityFields(!showCharityFields)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#FF8A3D',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                {showCharityFields ? '\u25BC' : '\u25B6'} Running for a cause?
              </button>
              {showCharityFields && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  <input
                    type="text"
                    value={charityName}
                    onChange={(e) => setCharityName(e.target.value)}
                    placeholder="Charity name"
                    maxLength={100}
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '0.9rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  <input
                    type="url"
                    value={fundraisingUrl}
                    onChange={(e) => setFundraisingUrl(e.target.value)}
                    placeholder="Fundraising page URL (e.g. gofundme.com/...)"
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.3)',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '0.9rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                onClick={() => handleConfirmRegistration(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#8b9cb3',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Not Yet
              </button>
              <button
                onClick={() => handleConfirmRegistration(true)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#FF8A3D',
                  border: 'none',
                  color: '#0B0B10',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 600
                }}
              >
                Yes, I Registered
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showLoginModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="auth-modal-title"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '20px',
              padding: '2.5rem',
              maxWidth: '420px',
              width: '90%',
              textAlign: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Logo */}
            <img
              src={`${process.env.PUBLIC_URL}/runrora-logo.png`}
              alt="Runrora"
              style={{
                height: '44px',
                width: 'auto',
                marginBottom: '1.5rem'
              }}
            />

            <h2 id="auth-modal-title" style={{
              color: '#FFFFFF',
              marginBottom: '0.5rem',
              fontSize: '1.5rem'
            }}>
              {isSignUp ? 'Get Started' : 'Welcome Back'}
            </h2>

            <p style={{
              color: '#8b9cb3',
              marginBottom: '1.5rem',
              lineHeight: 1.6
            }}>
              {isSignUp
                ? 'Create an account to save your race prep across devices.'
                : 'Sign in to access your races.'}
            </p>

            {authError && (
              <div role="alert" style={{
                background: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#ff6b6b',
                fontSize: '0.9rem'
              }}>
                {authError}
              </div>
            )}

            <form onSubmit={handleAuthSubmit}>
              {isSignUp && (
                <input
                  type="text"
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                  placeholder="Your name"
                  aria-label="Your name"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    marginBottom: '0.75rem',
                    background: '#16161D',
                    border: '1px solid rgba(255, 138, 61, 0.2)',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  autoFocus
                />
              )}
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Email address"
                aria-label="Email address"
                required
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  marginBottom: '0.75rem',
                  background: '#16161D',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                autoFocus={!isSignUp}
              />
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Password"
                aria-label="Password"
                required
                minLength={6}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  marginBottom: isSignUp ? '0.75rem' : '1.5rem',
                  background: '#16161D',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
              {isSignUp && (
                <select
                  value={loginCity}
                  onChange={(e) => setLoginCity(e.target.value)}
                  aria-label="Home city"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    marginBottom: '1.5rem',
                    background: '#16161D',
                    border: '1px solid rgba(255, 138, 61, 0.2)',
                    borderRadius: '8px',
                    color: loginCity ? '#FFFFFF' : '#8b9cb3',
                    fontSize: '1rem',
                    outline: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%238b9cb3' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 1rem center',
                  }}
                >
                  <option value="">Home city (optional)</option>
                  {['New York', 'Los Angeles', 'Chicago', 'Boston', 'San Francisco',
                    'Seattle', 'Austin', 'Denver', 'Portland', 'Miami',
                    'Atlanta', 'Washington DC', 'Philadelphia', 'San Diego', 'Nashville',
                    'Minneapolis', 'Charlotte', 'Houston', 'Phoenix', 'Honolulu'
                  ].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              )}
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '1rem 2rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: '#FF8A3D',
                  color: '#0B0B10',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {isSignUp ? "Let's Go" : 'Sign In'}
              </button>
            </form>

            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setAuthError('');
              }}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: 'none',
                color: '#FF8A3D',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
            </button>

            {/* Close button */}
            <button
              onClick={() => setShowLoginModal(false)}
              aria-label="Close sign in dialog"
              style={{
                display: 'block',
                margin: '0.5rem auto 0',
                padding: '0.5rem 1rem',
                background: 'transparent',
                border: 'none',
                color: '#8b9cb3',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid rgba(139, 156, 179, 0.15)',
        padding: '2rem 1.5rem',
        marginTop: '3rem',
        textAlign: 'center',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          marginBottom: '1rem',
        }}>
          <button
            onClick={() => setShowPrivacy(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#8b9cb3',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Privacy Policy
          </button>
          <button
            onClick={() => setShowSupport(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#8b9cb3',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Support
          </button>
          <button
            onClick={() => setShowBlog(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#8b9cb3',
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Blog
          </button>
        </div>
        <p style={{ color: '#5a6a7e', fontSize: '0.8rem', margin: 0 }}>
          &copy; {new Date().getFullYear()} Runrora. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default CommunityBrowser;
